import _omit from "lodash/omit";

import rollbar from "lib/rollbar";
import wsStorage from "lib/wsStorage";

import { APIError, del, get, patch, post } from "./fetch";

const DEFAULT_INTERVAL_MS = 1000;
const MAX_ATTEMPTS = 100;

class InvalidRequestError extends Error {
    constructor(message) {
        super(message);
        this.name = "InvalidRequestError";
    }
}

// This is used as a sort-of hacky way to ensure we don't create multiple queues for the same key
const queues = {};

const generateStore = () => {
    const queued = [];
    return {
        queued,
        started: false,
    };
};

export const generateRequest = (method, url, { params = {}, body = {}, options = {} }) => ({
    method,
    url,
    params,
    body,
    options,
    createdTime: new Date(),
    inflight: false,
    completed: false,
    result: null,
    error: null,
    attempts: 0,
    lastAttemptTime: null,
});

const executeRequest = async (request) => {
    const { method, url, params, body, options } = request;
    let reqFn;
    if (method.toLowerCase() === "get") {
        reqFn = () => get(url, params, options);
    } else if (method.toLowerCase() === "post") {
        reqFn = () => post(url, body, options);
    } else if (method.toLowerCase() === "delete") {
        reqFn = () => del(url, options);
    } else if (method.toLowerCase() === "patch") {
        reqFn = () => patch(url, body, options);
    }

    if (!reqFn) {
        throw new InvalidRequestError(`Request method ${method} is not supported`);
    }

    request.inflight = true;
    return reqFn();
};

export const createQueue = (
    key = "fetch-queue",
    { persist = true, maxAttempts = MAX_ATTEMPTS, intervalMS = DEFAULT_INTERVAL_MS } = {},
) => {
    if (queues[key]) {
        throw new Error(`Attempted to initialize already initialized queue for key ${key}`);
    }
    const store = (persist && wsStorage.getItem(key)) || generateStore();

    // Save all queued and not-completed requests to localstorage
    const persistStore = () => {
        if (persist) {
            const queued = store.queued
                .filter((r) => !r.completed)
                .map((r) => _omit(r, ["resolve", "reject"]));
            wsStorage.setItem(key, { queued });
        }
    };

    const resolveRequest = (request, result) => {
        request.inflight = false;
        request.result = result;
        request.completed = true;

        // Requests queued directly from localstorage won't have a promise
        if (request.resolve) {
            request.resolve(result);
        }
    };

    const rejectRequest = (request, err, result = null) => {
        request.inflight = false;
        request.error = err;
        request.result = result;
        request.completed = true;

        // Requests queued directly from localstorage won't have a promise
        if (request.reject) {
            request.reject(err.message);
        }
    };

    let stopTimeout = null;

    const processLoop = async () => {
        if (store.processing) return;
        store.processing = true;

        // Use shift to safely remove completed requests from the front of the queue
        while (store.queued[0]?.completed) {
            store.queued.shift();
        }

        for (let i = 0; i < store.queued.length; i += 1) {
            const request = store.queued[i];
            if (request.completed || request.inflight) continue;

            // Linear backoff of 1s * attempts
            const MS_SINCE_LAST = request.lastAttemptTime
                ? new Date() - request.lastAttemptTime
                : null;
            if (MS_SINCE_LAST && MS_SINCE_LAST < 1000 * request.attempts) {
                continue;
            }

            // Execute the request and handle the result
            request.attempts += 1;
            request.lastAttemptTime = new Date();
            try {
                // eslint-disable-next-line no-await-in-loop
                const result = await executeRequest(request);
                resolveRequest(request, result);
            } catch (err) {
                request.inflight = false;
                // If the request is invalid in some way then reject immediately
                if (err instanceof InvalidRequestError) {
                    rejectRequest(request, err);
                }

                // If we get an error from the api then reject immediately with result
                if (
                    err instanceof APIError &&
                    err.response.status >= 400 &&
                    err.response.status < 500
                ) {
                    rejectRequest(request, err, err.json || err.response.statusText);
                }

                // If we've hit max attempts then reject with custom error message
                if (request.attempts >= maxAttempts) {
                    rejectRequest(
                        request,
                        new Error(
                            `Max attempts reached for request [${request.method}] ${request.url}`,
                        ),
                    );
                }
            } finally {
                persistStore();
            }
        }

        store.processing = false;
    };

    const runLoop = async () => {
        if (store.processing) return;

        await processLoop();

        // Check to see if the queue was stopped
        if (!store.started || store.queued.length === 0) return;

        stopTimeout = setTimeout(() => {
            doRun(); // eslint-disable-line no-use-before-define
        }, intervalMS);
    };

    const doRun = () =>
        runLoop().catch((err) => rollbar.error(`Request queue loop failure: "${key}"`, err));

    const startQueue = () => {
        if (store.started) return;
        store.started = true;
        doRun();
    };

    // This will stop the timeout chain, but any existing process loop will run until completion
    // NOTE: It *is* possible to create two loops if you stop and restart under certain conditions
    // so it is currently not recommended to restart a queue
    const stopQueue = () => {
        store.started = false;
        clearTimeout(stopTimeout);
    };

    // Add a new request to the queue; return a promise that will have the result of the request
    // or error in the case of a failure
    const add = (request) =>
        new Promise((resolve, reject) => {
            store.queued.push({ ...request, resolve, reject });
            persistStore();
            // Immediately kick off a run when something is added
            doRun();
        });

    queues[key] = {
        add,
        startQueue,
        stopQueue,
        ...store,
    };

    return queues[key];
};

export const getQueue = (key, options) => queues[key] || createQueue(key, options || {});
